import { apiMixins } from "./api";
import { updateUploadJob } from "../util/uploadIndexedDb";
import {
  buildUploadClientTelemetryPayload,
  CLIENT_TELEMETRY_SCHEMA_VERSION,
  markUploadTelemetryFlushed,
  uploadDiagnosticLog,
} from "../util/uploadDiagnostics";

const KEEP_ALIVE_INTERVAL_MS = 30000;
const TERMINAL_API_RETRY_DELAYS_MS = [500, 1000, 2000];
const NOTIFICATION_ENDPOINT = "notifications";

function isBrowserFetchResourceError(error) {
  const message = error?.message || String(error || "");
  return error instanceof TypeError && (
    message.includes("Failed to fetch") ||
    message.includes("ERR_INSUFFICIENT_RESOURCES")
  );
}

async function sleep(ms) {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

export function isTransientUploadError(error) {
  const message = error?.message || String(error || "");
  return (
    error?.code === "UPLOAD_TRANSIENT" ||
    (error instanceof TypeError && message.includes("Failed to fetch")) ||
    message.includes("ERR_INSUFFICIENT_RESOURCES") ||
    message.includes("status 500") ||
    message.includes("status 502") ||
    message.includes("status 503") ||
    message.includes("status 504") ||
    message.includes("internal error") ||
    error?.name === "AbortError"
  );
}

export function classifyUploadError(error) {
  if (!error) {
    return { code: "UPLOAD_UNKNOWN", message: "Upload failed." };
  }
  if (error.name === "AbortError") {
    return { code: "UPLOAD_ABORTED", message: "Upload cancelled." };
  }
  if (error.code) {
    return { code: error.code, message: error.message || error.code };
  }
  const message = error.message || String(error);
  if (message.includes("maximum upload size")) {
    return { code: "UPLOAD_FILE_TOO_LARGE", message };
  }
  if (message.includes("status 403") || message.includes("status 401")) {
    return { code: "UPLOAD_FORBIDDEN", message };
  }
  if (message.includes("status 404")) {
    return { code: "UPLOAD_URL_NOT_FOUND", message };
  }
  if (isTransientUploadError(error)) {
    return { code: "UPLOAD_TRANSIENT", message };
  }
  return { code: "UPLOAD_FAILED", message };
}

export function isUploadJobAlreadyTerminalError(error) {
  const message = String(error?.message || error || "").toLowerCase();
  return message.includes("already terminal")
    || message.includes("already complete")
    || message.includes("job is terminal")
    || message.includes("job was terminal")
    || message.includes("not active");
}

export const uploadJobMixins = {
  mixins: [apiMixins],
  data() {
    return {
      uploadKeepAliveTimer: null,
      uploadKeepAliveJobId: null,
    };
  },
  methods: {
    async createUploadJob(manifest) {
      try {
        return await this.callAPI("item/upload-jobs", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          showLoader: false,
          body: JSON.stringify(manifest),
        });
      } catch (error) {
        uploadDiagnosticLog("warn", "upload-job-create-failed", {
          targetFolderId: manifest?.folderId,
          schemaVersion: manifest?.schemaVersion,
          entryCount: manifest?.summary?.entryCount,
          fileCount: manifest?.summary?.fileCount,
          folderCount: manifest?.summary?.folderCount,
          totalBytes: manifest?.summary?.totalBytes,
        }, error);
        throw error;
      }
    },
    async retryUploadJob(sourceJobId, manifest) {
      try {
        return await this.callAPI(`item/upload-jobs/${sourceJobId}/retry`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          showLoader: false,
          body: JSON.stringify(manifest),
        });
      } catch (error) {
        uploadDiagnosticLog("warn", "upload-job-retry-create-failed", {
          sourceUploadJobId: sourceJobId,
          targetFolderId: manifest?.folderId,
          schemaVersion: manifest?.schemaVersion,
          entryCount: manifest?.summary?.entryCount,
          fileCount: manifest?.summary?.fileCount,
          folderCount: manifest?.summary?.folderCount,
          totalBytes: manifest?.summary?.totalBytes,
        }, error);
        throw error;
      }
    },
    async sendUploadJobKeepAlive(jobId) {
      await this.callAPI(`item/upload-jobs/${jobId}/keep-alive`, {
        method: "POST",
        showLoader: false,
      });
      await updateUploadJob(jobId, { keepAliveLastSentAt: new Date().toISOString() });
      uploadDiagnosticLog("debug", "upload-job-keep-alive-sent", { uploadJobId: jobId });
    },
    startUploadJobKeepAlive(jobId) {
      this.stopUploadJobKeepAlive();
      this.uploadKeepAliveJobId = jobId;
      uploadDiagnosticLog("info", "upload-job-keep-alive-start", { uploadJobId: jobId });
      this.sendUploadJobKeepAlive(jobId).catch(console.error);
      this.uploadKeepAliveTimer = window.setInterval(() => {
        this.sendUploadJobKeepAlive(jobId).catch((error) => {
          console.error("Upload job keep-alive failed", error);
        });
      }, KEEP_ALIVE_INTERVAL_MS);
    },
    stopUploadJobKeepAlive() {
      if (this.uploadKeepAliveTimer) {
        window.clearInterval(this.uploadKeepAliveTimer);
        this.uploadKeepAliveTimer = null;
        uploadDiagnosticLog("info", "upload-job-keep-alive-stop", { uploadJobId: this.uploadKeepAliveJobId });
        this.uploadKeepAliveJobId = null;
      }
    },
    async cancelUploadJob(jobId) {
      uploadDiagnosticLog("info", "upload-job-cancel-api-start", { uploadJobId: jobId });
      try {
        await this.callAPI(`item/upload-jobs/${jobId}/cancel`, {
          method: "POST",
          showLoader: false,
          showErrorNotification: false,
        });
      } catch (error) {
        if (!isUploadJobAlreadyTerminalError(error)) {
          throw error;
        }
        uploadDiagnosticLog("info", "upload-job-cancel-api-already-terminal", { uploadJobId: jobId }, error);
      }
      await this.fetchAndDisplayUploadCompletionNotifications().catch((error) => {
        console.error("Immediate notification fetch failed", error);
      });
      uploadDiagnosticLog("info", "upload-job-cancel-api-complete", { uploadJobId: jobId });
    },
    async abandonUploadJob(jobId) {
      uploadDiagnosticLog("info", "upload-job-abandon-api-start", { uploadJobId: jobId });
      try {
        await this.callAPI(`item/upload-jobs/${jobId}/abandon`, {
          method: "POST",
          showLoader: false,
          showErrorNotification: false,
        });
      } catch (error) {
        if (!isUploadJobAlreadyTerminalError(error)) {
          throw error;
        }
        uploadDiagnosticLog("info", "upload-job-abandon-api-already-terminal", { uploadJobId: jobId }, error);
      }
      await this.fetchAndDisplayUploadCompletionNotifications().catch((error) => {
        console.error("Immediate notification fetch failed", error);
      });
      uploadDiagnosticLog("info", "upload-job-abandon-api-complete", { uploadJobId: jobId });
    },
    async completeUploadJob(jobId) {
      uploadDiagnosticLog("info", "upload-job-complete-api-start", { uploadJobId: jobId });
      for (let attempt = 0; attempt <= TERMINAL_API_RETRY_DELAYS_MS.length; attempt++) {
        try {
          await this.callAPI(`item/upload-jobs/${jobId}/complete`, {
            method: "POST",
            showLoader: false,
          });
          break;
        } catch (error) {
          if (!isBrowserFetchResourceError(error) || attempt >= TERMINAL_API_RETRY_DELAYS_MS.length) {
            throw error;
          }
          uploadDiagnosticLog("warn", "upload-job-complete-api-retry", {
            uploadJobId: jobId,
            attempt: attempt + 1,
            retryDelayMs: TERMINAL_API_RETRY_DELAYS_MS[attempt],
          }, error);
          await sleep(TERMINAL_API_RETRY_DELAYS_MS[attempt]);
        }
      }
      await this.fetchAndDisplayUploadCompletionNotifications().catch((error) => {
        console.error("Immediate notification fetch failed", error);
      });
      uploadDiagnosticLog("info", "upload-job-complete-api-complete", { uploadJobId: jobId });
    },
    async fetchAndDisplayUploadCompletionNotifications() {
      const notifications = await this.callAPI(NOTIFICATION_ENDPOINT, {
        method: "GET",
        showLoader: false,
        showErrorNotification: false,
      });
      if (!Array.isArray(notifications)) {
        return;
      }
      for (const notification of notifications) {
        const expiryTime = notification.autoDismiss ? 30000 : 86400000;
        const state = notification.state;
        const type = state === "SUCCESS"
          ? "success"
          : state === "INFO"
            ? "info"
            : state === "WARNING"
              ? "warning"
              : (state === "FAILURE" || state === "ERROR") ? "error" : null;
        if (type) {
          await this.$store.dispatch("addMessage", { type, label: notification.message, expiryTime });
        }
        await this.callAPI(`${NOTIFICATION_ENDPOINT}/${notification.id}/dismiss`, {
          method: "POST",
          showLoader: false,
          showErrorNotification: false,
        });
      }
    },
    async flushUploadJobClientTelemetry(jobId, terminalStatus, context = {}) {
      if (!jobId || !terminalStatus || !markUploadTelemetryFlushed(jobId, terminalStatus)) {
        return null;
      }
      const payload = buildUploadClientTelemetryPayload({
        uploadJobId: jobId,
        terminalStatus,
        ...context,
      });
      const clientTelemetryId = `${jobId}-${terminalStatus}-${Date.now()}`;
      try {
        return await this.callAPI(`item/upload-jobs/${jobId}/telemetry`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          showLoader: false,
          body: JSON.stringify({
            clientTelemetryId,
            clientTelemetrySchemaVersion: CLIENT_TELEMETRY_SCHEMA_VERSION,
            terminalStatus,
            payload,
          }),
        });
      } catch (error) {
        uploadDiagnosticLog("warn", "upload-client-telemetry-flush-failed", {
          uploadJobId: jobId,
          terminalStatus,
        }, error);
        return null;
      }
    },
    async reportUploadEntryError(jobId, logId, error, extra = {}) {
      const classification = classifyUploadError(error);
      await this.callAPI(`item/upload-jobs/${jobId}/entries/${logId}/error`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        showLoader: false,
        body: JSON.stringify({
          errorCode: classification.code,
          errorMessage: classification.message,
          technicalMessage: error?.stack || classification.message,
          ...extra,
        }),
      });
    },
    async createUploadSession(jobId, logId, request) {
      return await this.callAPI(`item/upload-jobs/${jobId}/entries/${logId}/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        showLoader: false,
        body: JSON.stringify(request),
      });
    },
    async recordUploadChunkResult(jobId, logId, chunkId, request) {
      return await this.callAPI(`item/upload-jobs/${jobId}/entries/${logId}/chunks/${chunkId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        showLoader: false,
        body: JSON.stringify(request),
      });
    },
    async reconcileUploadChunks(jobId, logId) {
      return await this.callAPI(`item/upload-jobs/${jobId}/entries/${logId}/chunks/reconcile`, {
        method: "POST",
        showLoader: false,
      });
    },
  },
  beforeDestroy() {
    this.stopUploadJobKeepAlive();
  },
};
